import {
  Industry,
  ApplicationFormModalContent,
  ApprovalStatus,
} from 'src/types';

import financeSuccessIllustration from 'src/assets/images/illustrations/finance/success-illustration.png';
import financeLockIllustration from 'src/assets/images/illustrations/finance/lock-illustration.png';
import financeErrorIllustration from 'src/assets/images/illustrations/finance/error-illustration.png';
import financeModalStyles from 'src/styles/finance/applicationFormModal.module.css';

import insuranceSuccessIllustration from 'src/assets/images/illustrations/insurance/success-illustration.png';
import insuranceLockIllustration from 'src/assets/images/illustrations/insurance/lock-illustration.png';
import insuranceErrorIllustration from 'src/assets/images/illustrations/insurance/error-illustration.png';
import insuranceModalStyles from 'src/styles/insurance/applicationFormModal.module.css';

import gamblingSuccessIllustration from 'src/assets/images/illustrations/gambling/success-illustration.png';
import gamblingLockIllustration from 'src/assets/images/illustrations/gambling/lock-illustration.png';
import gamblingErrorIllustration from 'src/assets/images/illustrations/gambling/error-illustration.png';
import gamblingModalStyles from 'src/styles/gambling/applicationFormModal.module.css';

import mobileSuccessIllustration from 'src/assets/images/illustrations/mobile/success-illustration.png';
import mobileLockIllustration from 'src/assets/images/illustrations/mobile/lock-illustration.png';
import mobileErrorIllustration from 'src/assets/images/illustrations/mobile/error-illustration.png';
import mobileModalStyles from 'src/styles/mobile/applicationFormModal.module.css';

import retailSuccessIllustration from 'src/assets/images/illustrations/retail/success-illustration.png';
import retailLockIllustration from 'src/assets/images/illustrations/retail/lock-illustration.png';
import retailErrorIllustration from 'src/assets/images/illustrations/retail/error-illustration.png';
import retailModalStyles from 'src/styles/retail/applicationFormModal.module.css';

const CONFIRMED_HEADER = 'You have been successfully verified!';
const CONFIRMED_BODY = 'Thank you for confirming your identity.';
const CONFIRMED_IMG_ALT = 'Success Illustration';

const PENDING_HEADER = 'Open the JustMe app and verify your identity';
const PENDING_BODY =
  "We need to make sure that it's really you applying. Open the JustMe app on your phone and follow the verification steps to confirm your identity";
const PENDING_IMG_ALT = 'Lock Illustration';

const LOADING_HEADER = 'Processing request';
const LOADING_BODY =
  "Please wait whilst we process your approval request";
const LOADING_IMG_ALT = 'Lock Illustration';

const DENIED_HEADER = 'Verification failed';
const DENIED_BODY =
  "We are sorry, but we haven't been able to progress your application at this point";
const DENIED_IMAGE_ALT = 'Failed Illustration';

const BACK_TO_HOME_BUTTON = 'Back to home';

const APPLICATION_FORM_MODAL_CONFIRMED_CONTENT_MAP: Record<
  Industry,
  ApplicationFormModalContent
> = {
  Finance: {
    cssStyles: financeModalStyles,
    header: CONFIRMED_HEADER,
    body: CONFIRMED_BODY,
    image: { src: financeSuccessIllustration, alt: CONFIRMED_IMG_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Gambling: {
    cssStyles: gamblingModalStyles,
    header: CONFIRMED_HEADER,
    body: CONFIRMED_BODY,
    image: { src: gamblingSuccessIllustration, alt: CONFIRMED_IMG_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Insurance: {
    cssStyles: insuranceModalStyles,
    header: CONFIRMED_HEADER,
    body: CONFIRMED_BODY,
    image: { src: insuranceSuccessIllustration, alt: CONFIRMED_IMG_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Mobile: {
    cssStyles: mobileModalStyles,
    header: CONFIRMED_HEADER,
    body: CONFIRMED_BODY,
    image: { src: mobileSuccessIllustration, alt: CONFIRMED_IMG_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Retail: {
    cssStyles: retailModalStyles,
    header: CONFIRMED_HEADER,
    body: CONFIRMED_BODY,
    image: { src: retailSuccessIllustration, alt: CONFIRMED_IMG_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
};

const APPLICATION_FORM_MODAL_PENDING_CONTENT_MAP: Record<
  Industry,
  ApplicationFormModalContent
> = {
  Finance: {
    cssStyles: financeModalStyles,
    header: PENDING_HEADER,
    body: PENDING_BODY,
    image: { src: financeLockIllustration, alt: PENDING_IMG_ALT },
  },
  Gambling: {
    cssStyles: gamblingModalStyles,
    header: PENDING_HEADER,
    body: PENDING_BODY,
    image: { src: gamblingLockIllustration, alt: PENDING_IMG_ALT },
  },
  Insurance: {
    cssStyles: insuranceModalStyles,
    header: PENDING_HEADER,
    body: PENDING_BODY,
    image: { src: insuranceLockIllustration, alt: PENDING_IMG_ALT },
  },
  Mobile: {
    cssStyles: mobileModalStyles,
    header: PENDING_HEADER,
    body: PENDING_BODY,
    image: { src: mobileLockIllustration, alt: PENDING_IMG_ALT },
  },
  Retail: {
    cssStyles: retailModalStyles,
    header: PENDING_HEADER,
    body: PENDING_BODY,
    image: { src: retailLockIllustration, alt: PENDING_IMG_ALT },
  },
};

const APPLICATION_FORM_MODAL_LOADING_CONTENT_MAP: Record<
  Industry,
  ApplicationFormModalContent
> = {
  Finance: {
    cssStyles: financeModalStyles,
    header: LOADING_HEADER,
    body: LOADING_BODY,
    image: { src: financeLockIllustration, alt: LOADING_IMG_ALT },
  },
  Gambling: {
    cssStyles: gamblingModalStyles,
    header: LOADING_HEADER,
    body: LOADING_BODY,
    image: { src: gamblingLockIllustration, alt: LOADING_IMG_ALT },
  },
  Insurance: {
    cssStyles: insuranceModalStyles,
    header: LOADING_HEADER,
    body: LOADING_BODY,
    image: { src: insuranceLockIllustration, alt: LOADING_IMG_ALT },
  },
  Mobile: {
    cssStyles: mobileModalStyles,
    header: LOADING_HEADER,
    body: LOADING_BODY,
    image: { src: mobileLockIllustration, alt: LOADING_IMG_ALT },
  },
  Retail: {
    cssStyles: retailModalStyles,
    header: LOADING_HEADER,
    body: LOADING_BODY,
    image: { src: retailLockIllustration, alt: LOADING_IMG_ALT },
  },
};

const APPLICATION_FORM_MODAL_DENIED_CONTENT_MAP: Record<
  Industry,
  ApplicationFormModalContent
> = {
  Finance: {
    cssStyles: financeModalStyles,
    header: DENIED_HEADER,
    body: DENIED_BODY,
    image: { src: financeErrorIllustration, alt: DENIED_IMAGE_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Gambling: {
    cssStyles: gamblingModalStyles,
    header: DENIED_HEADER,
    body: DENIED_BODY,
    image: { src: gamblingErrorIllustration, alt: DENIED_IMAGE_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Insurance: {
    cssStyles: insuranceModalStyles,
    header: DENIED_HEADER,
    body: DENIED_BODY,
    image: { src: insuranceErrorIllustration, alt: DENIED_IMAGE_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Mobile: {
    cssStyles: mobileModalStyles,
    header: DENIED_HEADER,
    body: DENIED_BODY,
    image: { src: mobileErrorIllustration, alt: DENIED_IMAGE_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
  Retail: {
    cssStyles: retailModalStyles,
    header: DENIED_HEADER,
    body: DENIED_BODY,
    image: { src: retailErrorIllustration, alt: DENIED_IMAGE_ALT },
    button: BACK_TO_HOME_BUTTON,
  },
};

const SERVICE_ERROR_HEADER = 'Oops';
const SERVICE_ERROR_BODY =
  "Something went wrong and we couldn't process your request";

export const APPLICATION_FORM_MODAL_SERVICE_ERROR_CONTENT_MAP: Record<
  Industry,
  ApplicationFormModalContent
> = {
  Finance: {
    cssStyles: financeModalStyles,
    header: SERVICE_ERROR_HEADER,
    body: SERVICE_ERROR_BODY,
    button: BACK_TO_HOME_BUTTON,
  },
  Gambling: {
    cssStyles: gamblingModalStyles,
    header: SERVICE_ERROR_HEADER,
    body: SERVICE_ERROR_BODY,
    button: BACK_TO_HOME_BUTTON,
  },
  Insurance: {
    cssStyles: insuranceModalStyles,
    header: SERVICE_ERROR_HEADER,
    body: SERVICE_ERROR_BODY,
    button: BACK_TO_HOME_BUTTON,
  },
  Mobile: {
    cssStyles: mobileModalStyles,
    header: SERVICE_ERROR_HEADER,
    body: SERVICE_ERROR_BODY,
    button: BACK_TO_HOME_BUTTON,
  },
  Retail: {
    cssStyles: retailModalStyles,
    header: SERVICE_ERROR_HEADER,
    body: SERVICE_ERROR_BODY,
    button: BACK_TO_HOME_BUTTON,
  },
};

export const APPLICATION_FORM_MODAL_CONTENT_MAP: Record<
  ApprovalStatus,
  Record<Industry, ApplicationFormModalContent>
> = {
  loading: APPLICATION_FORM_MODAL_LOADING_CONTENT_MAP,
  pending: APPLICATION_FORM_MODAL_PENDING_CONTENT_MAP,
  confirmed: APPLICATION_FORM_MODAL_CONFIRMED_CONTENT_MAP,
  denied: APPLICATION_FORM_MODAL_DENIED_CONTENT_MAP,
};
